// components/custom-ui/DocumentPreviewShell.tsx
// Shared chrome for previewing a document in a slide-over: header (icon, title,
// subtitle, caller actions, expand/collapse, close) + a scrollable body + an
// optional footer. Extracted from the sources SourceViewerModal so any preview
// (a Bibliotek source, a candidate CV, …) shares one look. The shell owns only
// presentation + the expand state; the CONTENT and how it's fetched stay with the
// caller — a Source uses an auth-blob fetch, a public CV uses a plain <iframe src>.
import React, { useState } from 'react';
import { X, Maximize2, Minimize2 } from 'lucide-react';
import { IslandSlideOver } from '@/components/custom-ui/IslandSlideOver';

interface DocumentPreviewShellProps {
    /** Leading header icon (e.g. <SourceIcon/> or <FileText/>). */
    icon?: React.ReactNode;
    title: string;
    /** Small muted line under the title (kind · size · date, or free text). */
    subtitle?: React.ReactNode;
    /** Extra header actions rendered before the expand/close buttons (e.g. "Öppna i ny flik"). */
    headerActions?: React.ReactNode;
    /** Optional footer under the body (e.g. the sources "Används i N moduler" panel). */
    footer?: React.ReactNode;
    widthClass?: string;
    zClass?: string;
    onClose: () => void;
    /** The preview body — an iframe / img / pre supplied by the caller. */
    children: React.ReactNode;
}

export const DocumentPreviewShell: React.FC<DocumentPreviewShellProps> = ({
    icon,
    title,
    subtitle,
    headerActions,
    footer,
    widthClass = 'max-w-[1100px]',
    zClass = 'z-[100]',
    onClose,
    children,
}) => {
    // Right-side drawer by default; toggle to fill the screen for big docs.
    const [expanded, setExpanded] = useState(false);

    return (
        <IslandSlideOver onClose={onClose} fullScreen={expanded} widthClass={widthClass} zClass={zClass}>
            {/* Header */}
            <div className="flex items-center gap-3 p-4 border-b border-gray-100 flex-shrink-0">
                {icon && <div className="flex-shrink-0">{icon}</div>}
                <div className="flex-1 min-w-0">
                    <h2 className="text-base font-semibold text-gray-900 truncate">{title}</h2>
                    {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
                </div>
                {headerActions}
                <button
                    onClick={() => setExpanded((v) => !v)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                    title={expanded ? 'Förminska' : 'Helskärm'}
                >
                    {expanded ? (
                        <Minimize2 className="w-5 h-5 text-gray-500" />
                    ) : (
                        <Maximize2 className="w-5 h-5 text-gray-500" />
                    )}
                </button>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" title="Stäng (Esc)">
                    <X className="w-5 h-5 text-gray-500" />
                </button>
            </div>

            {/* Body */}
            <div className="flex-1 min-h-0 overflow-hidden bg-gray-50">{children}</div>

            {footer}
        </IslandSlideOver>
    );
};

export default DocumentPreviewShell;
